import _ from 'lodash';
import * as uuid from 'uuid';
import { parseStringToNumberOrUndefined } from 'src/domain/parseNumber';
import { PjmLoadReductionType, PjmLocationStatus } from 'src/domain/pjm/dto/enums';
import { PjmBatteryInfo } from 'src/domain/pjm/dto/PjmBatteryInfo';
import { PjmGeneratorInfo } from 'src/domain/pjm/dto/PjmGeneratorInfo';
import { CreatePjmLocationDto, PjmLocationDto, UpdatePjmLocationDto } from 'src/domain/pjm/dto/PjmLocationDto';
import { PjmLocationReductionInfo } from 'src/domain/pjm/dto/PjmLocationReductionInfo';
import { SyncStatus } from 'src/domain/pjm/dto/SyncStatus';
import { Subset } from 'src/domain/Subset';

export interface PjmLocationForm extends PjmLocationDto {
    reductionInfoMap: Record<PjmLoadReductionType, number | null>;
    generatorInfo: Array<PjmGeneratorInfo & { _id?: string }>;
    batteryInfo: Array<PjmBatteryInfo & { _id?: string }>;
}

export class PjmLocationTransformer {
    static toFormValues(data?: PjmLocationDto): PjmLocationForm {
        const reductionInfoMap: Record<PjmLoadReductionType, number | null> = {
            [PjmLoadReductionType.BATTERIES]: 0,
            [PjmLoadReductionType.GENERATOR]: 0,
            [PjmLoadReductionType.HVAC]: 0,
            [PjmLoadReductionType.LIGHTING]: 0,
            [PjmLoadReductionType.MANUFACTURING]: 0,
            [PjmLoadReductionType.PLUG_LOAD]: 0,
            [PjmLoadReductionType.REFRIGERATION]: 0,
            [PjmLoadReductionType.WATER_HEATERS]: 0,
            [PjmLoadReductionType.COMPUTING]: 0,
        };

        if (!data) {
            return makeNewDefaultDto();
        }

        const reductionInfo = data.reductionInfo || [];
        reductionInfo.forEach(it => {
            if (it.reductionType) {
                reductionInfoMap[it.reductionType as PjmLoadReductionType] = it.reductionValue ?? null;
            }
        });

        const generatorInfo = (data.generatorInfo || []).map(it => {
            return { ...it, _id: uuid.v4() };
        });

        const batteryInfo = (data.batteryInfo || []).map(it => {
            return { ...it, _id: uuid.v4() };
        });

        const formData: PjmLocationForm = {
            ...data,
            reductionInfoMap,
            generatorInfo,
            batteryInfo,
            meterInfo: data.meterInfo || [],
        };

        return formData;
    }

    static toCreateDto(data: PjmLocationForm): CreatePjmLocationDto {
        const dto: CreatePjmLocationDto = {
            summaryInfo: {
                name: data.summaryInfo.name,
                CSP: {
                    name: data.summaryInfo.CSP.name,
                },
                EDC: {
                    name: data.summaryInfo.EDC.name,
                    accountNo: data.summaryInfo.EDC.accountNo,
                },
                zone: data.summaryInfo.zone,
                pricingPoint: data.summaryInfo.pricingPoint,
                status: data.summaryInfo.status,
                allowEDCtoSubmitData: data.summaryInfo.allowEDCtoSubmitData,
                allowLSEtoSubmitData: data.summaryInfo.allowLSEtoSubmitData,
            },
            siteInfo: data.siteInfo!,
            profileInfo: transformProfileInfo(data.profileInfo!),
            reductionInfo: transformReductionInfo(data.reductionInfoMap),

            generatorCount: data.generatorCount,
            generatorInfo: data.generatorInfo.map(it => _.omit(it, '_id')),
            batteryInfo: data.batteryInfo.map(it => _.omit(it, '_id')),
            meterInfo: data.meterInfo,
        };

        return dto;
    }

    static toUpdateDto(data: PjmLocationForm): UpdatePjmLocationDto {
        const dto: UpdatePjmLocationDto = {
            // uuid: data.uuid,
            summaryInfo: {
                name: data.summaryInfo.name,
                EDC: {
                    name: data.summaryInfo.EDC.name!,
                    accountNo: data.summaryInfo.EDC.accountNo!,
                },
                zone: data.summaryInfo.zone || null,
                pricingPoint: data.summaryInfo.pricingPoint || null,
                allowEDCtoSubmitData: Boolean(data.summaryInfo.allowEDCtoSubmitData),
                allowLSEtoSubmitData: Boolean(data.summaryInfo.allowLSEtoSubmitData),
            },
            siteInfo: data.siteInfo!,
            profileInfo: transformProfileInfo(data.profileInfo!),
            reductionInfo: transformReductionInfo(data.reductionInfoMap),

            generatorCount: data.generatorCount,
            generatorInfo: data.generatorInfo.map(it => _.omit(it, '_id')),
            batteryInfo: data.batteryInfo.map(it => _.omit(it, '_id')),
            meterInfo: data.meterInfo,
        };

        return dto;
    }
}

function makeNewDefaultDto() {
    const reductionInfoMap: Record<PjmLoadReductionType, number | null> = {
        [PjmLoadReductionType.BATTERIES]: 0,
        [PjmLoadReductionType.GENERATOR]: 0,
        [PjmLoadReductionType.HVAC]: 0,
        [PjmLoadReductionType.LIGHTING]: 0,
        [PjmLoadReductionType.MANUFACTURING]: 0,
        [PjmLoadReductionType.PLUG_LOAD]: 0,
        [PjmLoadReductionType.REFRIGERATION]: 0,
        [PjmLoadReductionType.WATER_HEATERS]: 0,
        [PjmLoadReductionType.COMPUTING]: 0,
    };

    const newLocationForm: Subset<PjmLocationForm> = {
        uuid: undefined,
        id: undefined,
        summaryInfo: {
            name: '',
            CSP: {
                name: 'ESPON', // ESPON is Enersponse?!
            },
            EDC: {
                name: undefined,
            },
            zone: undefined,
            pricingPoint: undefined,
            status: PjmLocationStatus.NEW,
            allowEDCtoSubmitData: false,
            allowLSEtoSubmitData: false,
        },
        siteInfo: {
            address: {
                address1: undefined,
                address2: undefined,
                city: undefined,
                state: undefined,
                zipCode: undefined,
            },
            businessSegment: undefined,
            utilityResidentialParticipant: false,
        },
        profileInfo: {
            energyLossFactor: undefined,
            capacityLossFactor: undefined,
            maxLoad: undefined,
            loadReduction: undefined,
            peakLoadContribution: undefined,
            winterPeakLoad: undefined,
        },
        reductionInfoMap,
        generatorCount: 0,
        generatorInfo: [],
        batteryInfo: [],
        rerraInfo: undefined,
        meterInfo: undefined,
        syncDrhubStatus: SyncStatus.PENDING,
        syncDrhubDate: undefined,
        syncDrhubError: undefined,
        date_created: undefined,
        date_modified: undefined,
        registrations: undefined,
    };

    return newLocationForm as PjmLocationForm;
}

function transformReductionInfo(reductionInfoMap: Record<PjmLoadReductionType, number | null>): PjmLocationReductionInfo[] {
    return Object.entries(reductionInfoMap).map(([key, val]) => {
        return {
            reductionType: key,
            reductionValue: parseStringToNumberOrUndefined(String(val)) ?? 0,
        } as unknown as PjmLocationReductionInfo;
    });
}

function transformProfileInfo<T extends Record<string, any>>(obj: T) {
    const res: any = {};

    Object.entries(obj).forEach(([key, val]) => {
        res[key] = parseStringToNumberOrUndefined(String(val)) ?? 0;
    });

    return res as T;
}
